import React, { useState } from "react";
import { fetchCategories, createCategory } from "@/lib/api";
import { Plus, Check } from "lucide-react";

export default function CategoryChips({
    space,           // "journal" | "notes"
    categories,      // list from server
    selectedId,      // for filter row: currently selected filter id, or "__all__" for all
    onSelect,        // (id|null|"__all__") => void
    variant = "filter", // "filter" | "picker"
    onCategoriesChanged,
}) {
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");

    const submitNew = async () => {
        const name = newName.trim();
        if (!name) { setCreating(false); return; }
        try {
            const cat = await createCategory({ space, name });
            setNewName(""); setCreating(false);
            onSelect?.(cat.id);
            onCategoriesChanged?.();
        } catch (_e) { /* ignore */ }
    };

    return (
        <div
            data-testid={`category-chips-${variant}`}
            className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1"
        >
            {variant === "filter" && (
                <ChipBtn
                    testid="category-filter-all"
                    active={selectedId === "__all__" || selectedId == null}
                    onClick={() => onSelect("__all__")}
                >
                    All
                </ChipBtn>
            )}
            {variant === "picker" && (
                <ChipBtn
                    testid="category-picker-none"
                    active={selectedId == null}
                    onClick={() => onSelect(null)}
                >
                    Uncategorized
                </ChipBtn>
            )}
            {categories.map((c) => {
                const active = selectedId === c.id;
                return (
                    <ChipBtn
                        key={c.id}
                        testid={`category-${variant}-${c.id}`}
                        active={active}
                        color={c.color}
                        onClick={() => onSelect(c.id)}
                    >
                        <span
                            className="inline-block w-2 h-2 rounded-full mr-1.5"
                            style={{ background: c.color }}
                        />
                        {c.name}
                        {variant === "filter" && typeof c.entry_count === "number" && (
                            <span className="ml-1.5 font-ui text-[10px] text-ink-40">{c.entry_count}</span>
                        )}
                        {variant === "picker" && active && <Check size={12} className="ml-1" />}
                    </ChipBtn>
                );
            })}
            {variant === "picker" && (
                creating ? (
                    <input
                        autoFocus
                        data-testid="category-new-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") submitNew();
                            if (e.key === "Escape") { setCreating(false); setNewName(""); }
                        }}
                        onBlur={submitNew}
                        placeholder="New category…"
                        className="shrink-0 px-3 py-1.5 rounded-full bg-white border border-moss/40 font-ui text-[12px] outline-none focus:ring-2 focus:ring-moss/20"
                    />
                ) : (
                    <button
                        data-testid="category-new-open"
                        onClick={() => setCreating(true)}
                        className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-dashed border-ink-10 hover:border-moss/50 font-ui text-[12px] text-ink-70 hover:text-moss-deep transition"
                    >
                        <Plus size={12} />
                        New category
                    </button>
                )
            )}
        </div>
    );
}

function ChipBtn({ testid, active, color, children, onClick }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={
                "shrink-0 inline-flex items-center px-3 py-1.5 rounded-full font-ui text-[12px] transition min-h-[32px] " +
                (active
                    ? "bg-ink text-white shadow-sm"
                    : "bg-white text-ink-70 border border-ink-10 hover:text-ink hover:border-moss/40")
            }
            style={active && color ? { background: color, color: "#fff", borderColor: color } : undefined}
        >
            {children}
        </button>
    );
}

/** Fetch helper used by pages. */
export async function loadCategories(space) {
    return (await fetchCategories(space)).categories || [];
}
